const WHEEL_STEP = 40;
const WHEEL_GAP = 250;

class CarouselTrack extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track || typeof EmblaCarousel === 'undefined') return;
    if (this.track.children.length < 2) return;

    this.initialized = true;
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.prevButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.dotGroups = [...this.querySelectorAll('[data-carousel-dots]')];
    this.textGroups = [...this.querySelectorAll('[data-carousel-text-group]')];

    const breakpoints = this.dataset.breakpoints ? JSON.parse(this.dataset.breakpoints) : {};
    const viewport = this.track.parentElement;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Embla defaults its container to viewport.children[0]. Pass the track
    // explicitly so sections can put siblings (rules, overlays) inside the viewport.
    const emblaOptions = {
      align: this.dataset.align || 'start',
      container: this.track,
      loop: this.dataset.loop === 'true',
      dragFree: this.dataset.dragFree === 'true',
      slidesToScroll: this.dataset.slidesToScroll ? Number(this.dataset.slidesToScroll) : 1,
      breakpoints,
    };

    // Only set duration when reduced motion is on — Embla merges with
    // Object.keys, so an explicit undefined would overwrite its default.
    if (this.reducedMotion) emblaOptions.duration = 0;

    // Re-applied on every init, so a cart morph (which destroys and re-inits the
    // track) lands on the slide the latest markup names.
    const startIndex = Number(this.dataset.startIndex) || 0;
    if (startIndex > 0) emblaOptions.startIndex = startIndex;

    this.embla = EmblaCarousel(viewport, emblaOptions);

    this.prevButton?.addEventListener('click', () => this.embla.scrollPrev(), {
      signal,
    });
    this.nextButton?.addEventListener('click', () => this.embla.scrollNext(), {
      signal,
    });

    for (const group of this.dotGroups) {
      const dots = [...group.querySelectorAll('[data-carousel-dot]')];
      for (const [index, dot] of dots.entries()) {
        dot.addEventListener('click', () => this.embla.scrollTo(index), {
          signal,
        });
      }
    }

    this.embla.on('select', () => this.updateState());
    this.embla.on('reInit', () => this.updateState());

    // A track built while hidden (a closed drawer) measures a single snap and clamps
    // the start slide to 0. Embla re-measures once it has a size, so re-apply the
    // start slide then, unless the shopper has already dragged the track.
    if (startIndex > 0) {
      this.embla.on('pointerDown', () => (this.userScrolled = true));
      this.embla.on('reInit', () => {
        if (!this.userScrolled && this.embla.selectedScrollSnap() !== startIndex) this.embla.scrollTo(startIndex, true);
      });
    }
    this.updateState();

    // Embla moves the track with transforms, so a trackpad swipe or shift+wheel
    // never scrolls it natively. Opt-in, because vertical-only carousels and
    // page scroll must keep the wheel.
    if (this.dataset.wheel === 'true') {
      viewport.addEventListener('wheel', (event) => this.handleWheel(event), { signal, passive: false });
    }

    this.autoplay =this.dataset.autoplay === 'true';
    this.autoplaySpeed = (Number.parseInt(this.dataset.autoplaySpeed, 10) || 5) * 1000;
    this.focusWithin = false;
    this.editorPaused = false;

    this.addEventListener(
      'focusin',
      () => {
        this.focusWithin = true;
        this.stop();
      },
      { signal },
    );
    this.addEventListener(
      'focusout',
      (event) => {
        this.focusWithin = this.contains(event.relatedTarget);
        this.start();
      },
      { signal },
    );
    document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()), { signal });
    document.addEventListener(
      'shopify:block:select',
      () => {
        this.editorPaused = true;
        this.stop();
      },
      { signal },
    );
    document.addEventListener(
      'shopify:block:deselect',
      () => {
        this.editorPaused = false;
        this.start();
      },
      { signal },
    );

    this.start();
  }

  // Public seam for sections that drive scroll position from their own UI.
  // No-ops until Embla is initialized, so callers need no readiness check.
  scrollToIndex(index) {
    this.embla?.scrollTo(index);
  }

  // Steps one slide per WHEEL_STEP of horizontal travel. The gap between steps
  // keeps a trackpad's momentum tail from skipping past every slide at once.
  handleWheel(event) {
    const delta = event.shiftKey && !event.deltaX ? event.deltaY : event.deltaX;
    if (Math.abs(delta) <= Math.abs(event.shiftKey ? 0 : event.deltaY)) return;

    const forward = delta > 0;
    if (forward ? !this.embla.canScrollNext() : !this.embla.canScrollPrev()) return;

    event.preventDefault();
    this.userScrolled = true;
    this.wheelDelta = (this.wheelDelta || 0) + delta;
    if (Math.abs(this.wheelDelta) < WHEEL_STEP || performance.now() - (this.wheelAt || 0) < WHEEL_GAP) return;

    this.wheelDelta = 0;
    this.wheelAt = performance.now();
    if (forward) this.embla.scrollNext();
    else this.embla.scrollPrev();
  }

  start() {
    this.stop();
    if (!this.autoplay || this.reducedMotion || this.focusWithin || this.editorPaused || document.hidden) return;

    this.autoplayTimer = setInterval(() => this.embla.scrollNext(), this.autoplaySpeed);
  }

  stop() {
    clearInterval(this.autoplayTimer);
    this.autoplayTimer = undefined;
  }

  updateState() {
    if (!this.embla) return;

    if (this.prevButton) {
      const canPrev = this.embla.canScrollPrev();
      this.prevButton.hidden = !canPrev;
      this.prevButton.disabled = !canPrev;
    }

    if (this.nextButton) {
      const canNext = this.embla.canScrollNext();
      this.nextButton.hidden = !canNext;
      this.nextButton.disabled = !canNext;
    }

    const selected = this.embla.selectedScrollSnap();
    const snapCount = this.embla.scrollSnapList().length;
    for (const group of this.dotGroups) {
      const dots = [...group.querySelectorAll('[data-carousel-dot]')];
      const activeIndex = Math.min(selected, snapCount - 1);

      for (const [index, dot] of dots.entries()) {
        dot.hidden = index >= snapCount;
        if (index === activeIndex) {
          dot.setAttribute('data-active', '');
        } else {
          dot.removeAttribute('data-active');
        }
      }
    }

    for (const group of this.textGroups) {
      const panels = [...group.querySelectorAll('[data-carousel-text]')];
      const activeIndex = Math.min(selected, panels.length - 1);

      for (const [index, panel] of panels.entries()) {
        if (index === activeIndex) {
          panel.setAttribute('data-active', '');
        } else {
          panel.removeAttribute('data-active');
        }
      }
    }
  }

  destroyCarousel() {
    this.stop();
    this.controller?.abort();
    this.embla?.destroy();
    this.embla = undefined;
    this.initialized = false;
    this.userScrolled = false;
  }

  // Destroy first — connectedCallback bails early while initialized is set.
  reInit() {
    this.destroyCarousel();
    this.connectedCallback();
  }

  disconnectedCallback() {
    this.destroyCarousel();
  }
}

if (!customElements.get('carousel-track')) {
  customElements.define('carousel-track', CarouselTrack);
}
